import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Shell from "@/components/Shell";
import { Breadcrumbs } from "@/components/Shell";
import { Card, Btn, Input, Textarea, Label, Spinner } from "@/components/ui-kit";
import api from "@/lib/api";
import { formatApiError } from "@/lib/format";

const ClientForm = ({ mode = "new" }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "" });
  const [loading, setLoading] = useState(mode === "edit");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (mode === "edit") {
      api.get(`/clients/${id}`).then((r) => {
        setForm({
          name: r.data.name || "",
          email: r.data.email || "",
          phone: r.data.phone || "",
          address: r.data.address || "",
        });
      }).finally(() => setLoading(false));
    }
  }, [id, mode]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      if (mode === "new") {
        const { data } = await api.post("/clients", form);
        navigate(`/clients/${data.id}`);
      } else {
        await api.put(`/clients/${id}`, form);
        navigate(`/clients/${id}`);
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Shell><div className="flex justify-center py-20"><Spinner /></div></Shell>;

  return (
    <Shell>
      <Breadcrumbs items={[{ to: "/clients", label: "Clients" }, { label: mode === "new" ? "New Client" : "Edit Client" }]} />
      <h1 className="font-display text-4xl font-bold text-zinc-950 mb-1">{mode === "new" ? "New Client" : "Edit Client"}</h1>
      <p className="text-sm text-zinc-500 mb-8">{mode === "new" ? "Add a new client to your system" : "Update client details"}</p>

      <Card className="p-6 max-w-2xl">
        <form onSubmit={submit} className="space-y-4" data-testid="client-form">
          <h2 className="font-display text-lg font-semibold text-zinc-950">Client Details</h2>
          <div>
            <Label required>Name</Label>
            <Input required data-testid="client-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <Label>Email Address</Label>
            <Input type="email" data-testid="client-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div>
            <Label>Phone Number</Label>
            <Input data-testid="client-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div>
            <Label>Address</Label>
            <Textarea rows={4} data-testid="client-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex gap-2 pt-2">
            <Btn type="submit" disabled={saving} data-testid="client-save-btn">{mode === "new" ? "Create Client" : "Update Client"}</Btn>
            <Btn type="button" variant="secondary" onClick={() => navigate(-1)} data-testid="client-cancel-btn">Cancel</Btn>
          </div>
        </form>
      </Card>
    </Shell>
  );
};

export default ClientForm;
